import React from 'react';
import './Button.css';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  onClick, 
  loading = false, 
  disabled = false, 
  fullWidth = false,
  className = '',
  icon: Icon,
  iconPosition = 'left',
  rounded = 'xl',
  shadow = true,
  pulse = false
}) => {
  const baseStyles = 'btn inline-flex items-center justify-center gap-2.5 font-semibold transition-all duration-300 focus:outline-none relative overflow-hidden';
  
  const variants = {
    primary: 'btn-primary bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white hover:shadow-xl hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-blue-500/30',
    secondary: 'btn-secondary bg-[#0F172A] text-white hover:bg-[#1E293B] hover:shadow-xl hover:shadow-slate-900/25 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-slate-900/30',
    outline: 'btn-outline bg-transparent text-[#0F172A] border-2 border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB] hover:bg-blue-50/50 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-blue-500/20',
    success: 'btn-success bg-gradient-to-r from-[#10B981] to-[#059669] text-white hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-emerald-500/30',
    danger: 'btn-danger bg-gradient-to-r from-[#EF4444] to-[#DC2626] text-white hover:shadow-xl hover:shadow-red-500/30 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-red-500/30',
    warning: 'btn-warning bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white hover:shadow-xl hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-amber-500/30',
    ghost: 'btn-ghost bg-transparent text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-gray-400/20',
    whatsapp: 'btn-whatsapp bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white hover:shadow-xl hover:shadow-[#25D366]/30 hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-[#25D366]/30',
    glass: 'btn-glass bg-white/20 backdrop-blur-xl text-white border border-white/30 hover:bg-white/30 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] focus:ring-4 focus:ring-white/30',
    // neon removed - it had purple
    // neon: 'btn-neon bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] text-white ...' // ❌ REMOVED
  };

  const sizes = {
    xs: 'px-2.5 py-1 text-xs gap-1.5',
    sm: 'px-3.5 py-1.5 text-xs gap-1.5',
    md: 'px-5 py-2.5 text-sm gap-2',
    lg: 'px-7 py-3.5 text-base gap-2.5',
    xl: 'px-9 py-4.5 text-lg gap-3'
  };

  const roundedStyles = {
    none: 'rounded-none',
    sm: 'rounded-lg',
    md: 'rounded-xl',
    lg: 'rounded-2xl',
    xl: 'rounded-3xl',
    full: 'rounded-full'
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const shadowClass = shadow ? 'shadow-lg' : '';
  const pulseClass = pulse ? 'btn-pulse' : '';
  const loadingClass = loading ? 'btn-loading' : '';

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${roundedStyles[rounded]} ${widthClass} ${shadowClass} ${pulseClass} ${loadingClass} ${className}`}
      onClick={onClick}
      disabled={loading || disabled}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4 text-current flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {!loading && Icon && iconPosition === 'left' && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className="relative z-10">{children}</span>
      {!loading && Icon && iconPosition === 'right' && <Icon className="w-4 h-4 flex-shrink-0" />}
      
      {/* Shine effect */}
      <span className="btn-shine"></span>
    </button>
  );
};

export default Button;